/**
 * Dashboard Page
 * 
 * Overview of GPU platform metrics and recent activity.
 * Enterprise-grade professional design.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Cpu,
  DollarSign,
  Clock,
  Activity,
  Zap,
  Server,
  Globe,
  Timer,
  History,
  PlayCircle,
  XCircle,
  CheckCircle,
  AlertCircle,
  LogIn,
  LogOut,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  Layers,
} from 'lucide-react';
import { gpuJobsApi, activityApi, GpuJob, ActivityLog, profileApi } from '../lib/api';
import { formatDistanceToNow } from 'date-fns';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../stores/auth';
import { usePreferencesStore } from '../stores/preferences';
import clsx from 'clsx';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0 },
};

const ACTIVITY_PAGE_SIZE = 5;

export default function DashboardPage() {
  const [activityPage, setActivityPage] = useState(0);
  const [jobsPage, setJobsPage] = useState(1);
  const [jobsPerPage, setJobsPerPage] = useState(5);
  const [jobsDateFrom, setJobsDateFrom] = useState<string>('');
  const [jobsDateTo, setJobsDateTo] = useState<string>('');
  const { isAuthenticated } = useAuthStore();
  const preferencesStore = usePreferencesStore();

  // Load user preferences from profile when dashboard mounts
  useEffect(() => {
    if (isAuthenticated) {
      profileApi.getProfile()
        .then((profile) => {
          // Apply theme preference
          if (profile.theme) {
            preferencesStore.setTheme(profile.theme as 'dark' | 'light' | 'system');
          }
          
          // Apply compact mode preference
          if (profile.compactMode !== undefined) {
            preferencesStore.setCompactMode(profile.compactMode);
          }
          
          // Apply timezone preference
          if (profile.timezone) {
            preferencesStore.setTimezone(profile.timezone as any);
          }
          
          // Apply date format preference
          if (profile.dateFormat) {
            preferencesStore.setDateFormat(profile.dateFormat as any);
          }
        })
        .catch((error) => {
          // If preference loading fails, continue with defaults from localStorage
          console.warn('Failed to load user preferences on dashboard mount:', error);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['gpu-jobs'],
    queryFn: gpuJobsApi.list,
    refetchInterval: 30000,
  });

  const { data: instances = [], isLoading: instancesLoading } = useQuery({
    queryKey: ['running-instances'],
    queryFn: gpuJobsApi.getRunningInstances,
    refetchInterval: 15000,
  });

  const { data: activityResponse, isLoading: activityLoading } = useQuery({
    queryKey: ['activity-logs', activityPage],
    queryFn: () => activityApi.getRecent(ACTIVITY_PAGE_SIZE, activityPage * ACTIVITY_PAGE_SIZE),
    refetchInterval: 30000,
  });

  const activityLogs = activityResponse?.data || [];
  const activityPagination = activityResponse?.pagination;
  const totalActivityPages = activityPagination ? Math.ceil(activityPagination.total / ACTIVITY_PAGE_SIZE) : 0;

  // Calculate metrics
  const runningJobs = jobs.filter((j) => j.status === 'running');
  const totalSpend = jobs.reduce((acc, j) => acc + j.estimatedCostUsd, 0);
  const avgRuntime = jobs.length > 0
    ? jobs.reduce((acc, j) => acc + j.maxRuntimeHours, 0) / jobs.length
    : 0;
  const totalGpus = runningJobs.reduce((acc, j) => acc + j.gpuCount, 0);

  const stats = [
    {
      label: 'Active Jobs',
      value: runningJobs.length,
      subtext: 'Currently running',
      icon: Cpu,
      gradient: 'from-emerald-500 to-teal-600',
      bgGradient: 'from-emerald-500/10 to-teal-600/5',
      iconBg: 'bg-emerald-500/15',
      iconColor: 'text-emerald-400',
    },
    {
      label: 'Total Spend',
      value: `$${totalSpend.toFixed(2)}`,
      subtext: 'This period',
      icon: DollarSign,
      gradient: 'from-amber-500 to-orange-600',
      bgGradient: 'from-amber-500/10 to-orange-600/5',
      iconBg: 'bg-amber-500/15',
      iconColor: 'text-amber-400',
    },
    {
      label: 'Avg Runtime',
      value: `${avgRuntime.toFixed(1)}h`,
      subtext: 'Per job',
      icon: Clock,
      gradient: 'from-blue-500 to-indigo-600',
      bgGradient: 'from-blue-500/10 to-indigo-600/5',
      iconBg: 'bg-blue-500/15',
      iconColor: 'text-blue-400',
    },
    {
      label: 'GPUs Active',
      value: totalGpus,
      subtext: 'In use now',
      icon: Zap,
      gradient: 'from-violet-500 to-purple-600',
      bgGradient: 'from-violet-500/10 to-purple-600/5',
      iconBg: 'bg-violet-500/15',
      iconColor: 'text-violet-400',
    },
  ];

  // Filter and paginate jobs
  let filteredJobs = jobs;
  
  // Filter by date range
  if (jobsDateFrom || jobsDateTo) {
    filteredJobs = filteredJobs.filter(j => {
      if (!j.createdAt) return false;
      const jobDate = new Date(j.createdAt);
      const fromDate = jobsDateFrom ? new Date(jobsDateFrom) : null;
      const toDate = jobsDateTo ? new Date(jobsDateTo) : null;
      
      // Set time to start/end of day for proper comparison
      if (fromDate) fromDate.setHours(0, 0, 0, 0);
      if (toDate) toDate.setHours(23, 59, 59, 999);
      
      if (fromDate && jobDate < fromDate) return false;
      if (toDate && jobDate > toDate) return false;
      return true;
    });
  }
  
  // Calculate pagination
  const totalJobsPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const jobsStartIndex = (jobsPage - 1) * jobsPerPage;
  const jobsEndIndex = jobsStartIndex + jobsPerPage;
  const recentJobs = filteredJobs.slice(jobsStartIndex, jobsEndIndex);

  const getStatusBadge = (status: GpuJob['status']) => {
    const styles = {
      pending: 'bg-amber-500/15 text-amber-400 border-amber-500/25',
      provisioning: 'bg-blue-500/15 text-blue-400 border-blue-500/25',
      running: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25',
      stopping: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25',
      stopped: 'bg-orange-500/15 text-orange-400 border-orange-500/25',
      completed: 'bg-slate-500/15 text-slate-400 border-slate-500/25',
      failed: 'bg-red-500/15 text-red-400 border-red-500/25',
      cancelled: 'bg-slate-500/15 text-slate-400 border-slate-500/25',
      terminated: 'bg-slate-500/15 text-slate-400 border-slate-500/25',
    };
    return styles[status] || 'bg-slate-500/15 text-slate-400 border-slate-500/25';
  };

  const getActivityIcon = (action: string) => {
    const iconClass = "w-4 h-4";
    if (action.includes('created') || action.includes('started')) {
      return <PlayCircle className={`${iconClass} text-emerald-400`} />;
    }
    if (action.includes('cancelled') || action.includes('terminated')) {
      return <XCircle className={`${iconClass} text-orange-400`} />;
    }
    if (action.includes('completed')) {
      return <CheckCircle className={`${iconClass} text-blue-400`} />;
    }
    if (action.includes('failed')) {
      return <AlertCircle className={`${iconClass} text-red-400`} />;
    }
    if (action.includes('login')) {
      return <LogIn className={`${iconClass} text-cyan-400`} />;
    }
    if (action.includes('logout')) {
      return <LogOut className={`${iconClass} text-slate-400`} />;
    }
    if (action.includes('launched')) {
      return <Server className={`${iconClass} text-gpu-400`} />;
    }
    return <Activity className={`${iconClass} text-slate-400`} />;
  };

  const formatActivityAction = (action: string) => {
    return action
      .split('.')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="space-y-8 max-w-[1600px] mx-auto"
    >
      {/* Page Header */}
      <motion.header variants={itemVariants} className="pb-2">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-semibold tracking-tight text-white mb-2">
              Dashboard
            </h1>
            <p className="text-sm sm:text-base text-surface-400 leading-relaxed">
              Monitor your GPU computing resources, track spending, and manage workloads
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/jobs/new"
              className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              New Job
            </Link>
          </div>
        </div>
      </motion.header>

      {/* Overview Content */}
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="space-y-6"
      >
          {/* Stats Cards */}
          <motion.div variants={itemVariants} className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {stats.map((stat) => (
              <div
                key={stat.label}
                className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${stat.bgGradient} border border-surface-800/50 p-4 sm:p-6 transition-all duration-300 hover:border-surface-700/60`}
              >
                <div className="flex items-start justify-between mb-3 sm:mb-4">
                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${stat.iconBg} flex items-center justify-center`}>
                    <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.iconColor}`} />
                  </div>
                  <div className="text-right">
                    <p className="text-xl sm:text-3xl font-semibold text-white tracking-tight">{stat.value}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-surface-300">{stat.label}</p>
                  <p className="text-xs text-surface-500 mt-0.5">{stat.subtext}</p>
                </div>
              </div>
            ))}
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Left Column - 2/3 width */}
            <div className="lg:col-span-2 space-y-6">
              {/* Running Instances */}
              <motion.section variants={itemVariants} className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-surface-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center">
                      <Server className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-white">Running Instances</h2>
                      <p className="text-xs sm:text-sm text-surface-500">{instances.length} active</p>
                    </div>
                  </div>
                  <Link to="/jobs" className="text-xs sm:text-sm font-medium text-surface-400 hover:text-surface-200 transition-colors flex items-center gap-1">
                    View all
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                <div className="p-4 sm:p-6">
                  {instancesLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : instances.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-14 h-14 rounded-2xl bg-surface-800/50 flex items-center justify-center mx-auto mb-4">
                        <Server className="w-7 h-7 text-surface-600" />
                      </div>
                      <p className="text-surface-400 font-medium mb-2">No running instances</p>
                      <p className="text-sm text-surface-500">
                        Launch a GPU job to start an instance
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {instances.map((instance) => (
                        <div
                          key={instance.instanceId}
                          className="p-5 rounded-xl bg-surface-800/40 border border-surface-700/40 hover:border-surface-600/50 transition-all"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-3">
                                <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${
                                  instance.state === 'running'
                                    ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25'
                                    : instance.state === 'pending'
                                    ? 'bg-amber-500/15 text-amber-400 border-amber-500/25'
                                    : 'bg-slate-500/15 text-slate-400 border-slate-500/25'
                                }`}>
                                  <span className={`w-1.5 h-1.5 rounded-full ${
                                    instance.state === 'running' ? 'bg-emerald-400 animate-pulse' :
                                    instance.state === 'pending' ? 'bg-amber-400 animate-pulse' :
                                    'bg-slate-400'
                                  }`} />
                                  {instance.state}
                                </span>
                                <span className="font-mono text-sm text-surface-300 font-medium">
                                  {instance.instanceType}
                                </span>
                              </div>
                              <div className="font-mono text-sm text-gpu-400 mb-3">
                                {instance.instanceId}
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                {instance.publicIpAddress && (
                                  <div className="flex items-center gap-2 text-sm text-surface-400">
                                    <Globe className="w-4 h-4 text-surface-500" />
                                    <span className="font-mono text-surface-300">{instance.publicIpAddress}</span>
                                  </div>
                                )}
                                {instance.launchTime && (
                                  <div className="flex items-center gap-2 text-sm text-surface-400">
                                    <Clock className="w-4 h-4 text-surface-500" />
                                    <span>{formatDistanceToNow(new Date(instance.launchTime), { addSuffix: true })}</span>
                                  </div>
                                )}
                                {instance.ttl && (
                                  <div className="flex items-center gap-2 text-sm text-surface-400">
                                    <Timer className="w-4 h-4 text-surface-500" />
                                    <span>TTL: {formatDistanceToNow(new Date(instance.ttl), { addSuffix: true })}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            {instance.jobId && (
                              <Link
                                to={`/jobs/${instance.jobId}`}
                                className="flex-shrink-0 px-4 py-2 text-sm font-medium text-surface-300 bg-surface-700/50 hover:bg-surface-700 rounded-lg transition-colors"
                              >
                                View Job
                              </Link>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </motion.section>

              {/* Recent Jobs Table */}
              <motion.section variants={itemVariants} className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden">
                <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-surface-800/50 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gpu-500/15 flex items-center justify-center">
                      <Layers className="w-5 h-5 text-gpu-400" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-white">Recent Jobs</h2>
                      <p className="text-xs sm:text-sm text-surface-500">{filteredJobs.length} total</p>
                    </div>
                  </div>
                  <Link to="/jobs" className="text-xs sm:text-sm font-medium text-surface-400 hover:text-surface-200 transition-colors flex items-center gap-1">
                    View all
                    <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>

                {/* Date Range and Page Size Filters */}
                {jobs.length > 0 && (
                  <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-surface-800/50 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 flex-wrap">
                    {/* Date Range Filter */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Clock className="w-4 h-4 text-surface-400" />
                      <span className="text-sm text-surface-400">Date Range:</span>
                      <input
                        type="date"
                        value={jobsDateFrom}
                        onChange={(e) => {
                          setJobsDateFrom(e.target.value);
                          setJobsPage(1);
                        }}
                        className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                        placeholder="From"
                      />
                      <span className="text-surface-500">to</span>
                      <input
                        type="date"
                        value={jobsDateTo}
                        onChange={(e) => {
                          setJobsDateTo(e.target.value);
                          setJobsPage(1);
                        }}
                        className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors"
                        placeholder="To"
                      />
                      {(jobsDateFrom || jobsDateTo) && (
                        <button
                          onClick={() => {
                            setJobsDateFrom('');
                            setJobsDateTo('');
                            setJobsPage(1);
                          }}
                          className="px-2 py-1 rounded-lg text-xs bg-surface-700 text-surface-300 hover:bg-surface-600 transition-colors"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2 sm:ml-auto">
                      <span className="text-sm text-surface-400">Per page:</span>
                      <select
                        value={jobsPerPage}
                        onChange={(e) => {
                          setJobsPerPage(Number(e.target.value));
                          setJobsPage(1);
                        }}
                        className="px-3 py-1.5 rounded-lg text-sm bg-surface-800 border border-surface-700 text-surface-200 focus:outline-none focus:border-gpu-500 transition-colors cursor-pointer"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                )}

                {isLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="w-8 h-8 border-2 border-gpu-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : recentJobs.length === 0 ? (
                  <div className="text-center py-16">
                    <div className="w-14 h-14 rounded-2xl bg-surface-800/50 flex items-center justify-center mx-auto mb-4">
                      <Cpu className="w-7 h-7 text-surface-600" />
                    </div>
                    <p className="text-surface-400 font-medium mb-2">No jobs yet</p>
                    <Link to="/jobs/new" className="btn-primary mt-4 inline-flex items-center gap-2">
                      Launch Your First Job
                    </Link>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[500px]">
                      <thead>
                        <tr className="border-b border-surface-800/40">
                          <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider">Job ID</th>
                          <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider hidden sm:table-cell">Instance</th>
                          <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider">Status</th>
                          <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider">Cost</th>
                          <th className="text-left py-3 sm:py-4 px-4 sm:px-6 text-xs font-semibold text-surface-400 uppercase tracking-wider hidden md:table-cell">Created</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-surface-800/30">
                        {recentJobs.map((job) => (
                          <tr
                            key={job.id}
                            className="hover:bg-surface-800/30 transition-colors group"
                          >
                            <td className="py-3 sm:py-4 px-4 sm:px-6">
                              <Link
                                to={`/jobs/${job.id}`}
                                className="font-mono text-sm text-gpu-400 hover:text-gpu-300 font-medium transition-colors"
                              >
                                {job.id.slice(0, 8)}...
                              </Link>
                            </td>
                            <td className="py-3 sm:py-4 px-4 sm:px-6 hidden sm:table-cell">
                              <span className="font-mono text-sm text-surface-300">{job.instanceType}</span>
                            </td>
                            <td className="py-3 sm:py-4 px-4 sm:px-6">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusBadge(job.status)}`}>
                                {job.status}
                              </span>
                            </td>
                            <td className="py-3 sm:py-4 px-4 sm:px-6 text-sm text-surface-300 font-medium">
                              ${job.estimatedCostUsd.toFixed(2)}
                            </td>
                            <td className="py-3 sm:py-4 px-4 sm:px-6 text-sm text-surface-500 hidden md:table-cell">
                              {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination Controls */}
                    {totalJobsPages > 1 && (
                      <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-surface-800/50 flex flex-col sm:flex-row items-center justify-between gap-3">
                        <div className="text-sm text-surface-400">
                          Showing {jobsStartIndex + 1}-{Math.min(jobsEndIndex, filteredJobs.length)} of {filteredJobs.length} jobs
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setJobsPage(Math.max(1, jobsPage - 1))}
                            disabled={jobsPage === 1}
                            className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-4 h-4" />
                          </button>
                          <div className="flex items-center gap-1">
                            {Array.from({ length: totalJobsPages }, (_, i) => i + 1).map((page) => {
                              // Show first, last, current, and pages around current
                              if (
                                page === 1 ||
                                page === totalJobsPages ||
                                (page >= jobsPage - 1 && page <= jobsPage + 1)
                              ) {
                                return (
                                  <button
                                    key={page}
                                    onClick={() => setJobsPage(page)}
                                    className={clsx(
                                      'px-3 py-1 rounded-lg text-sm font-medium transition-colors',
                                      page === jobsPage
                                        ? 'bg-gpu-500 text-white'
                                        : 'bg-surface-800 text-surface-300 hover:bg-surface-700'
                                    )}
                                  >
                                    {page}
                                  </button>
                                );
                              } else if (
                                page === jobsPage - 2 ||
                                page === jobsPage + 2
                              ) {
                                return <span key={page} className="text-surface-500">...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <button
                            onClick={() => setJobsPage(Math.min(totalJobsPages, jobsPage + 1))}
                            disabled={jobsPage === totalJobsPages}
                            className="p-2 rounded-lg bg-surface-800 text-surface-300 hover:bg-surface-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </motion.section>
            </div>

            {/* Right Column - Activity Feed */}
            <div className="lg:col-span-1">
              <motion.section variants={itemVariants} className="bg-surface-900/80 border border-surface-800/60 rounded-2xl overflow-hidden sticky top-6">
                <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-surface-800/50">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-500/15 flex items-center justify-center">
                      <History className="w-5 h-5 text-cyan-400" />
                    </div>
                    <div>
                      <h2 className="text-base sm:text-lg font-semibold text-white">Activity</h2>
                      <p className="text-xs sm:text-sm text-surface-500">
                        {activityPagination?.total || 0} events
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  {activityLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : activityLogs.length === 0 && activityPage === 0 ? (
                    <div className="text-center py-12">
                      <div className="w-12 h-12 rounded-xl bg-surface-800/50 flex items-center justify-center mx-auto mb-4">
                        <History className="w-6 h-6 text-surface-600" />
                      </div>
                      <p className="text-surface-400 text-sm font-medium mb-1">No activity yet</p>
                      <p className="text-surface-500 text-xs">
                        Activity will appear as you use the platform
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {activityLogs.map((activity: ActivityLog) => (
                          <div
                            key={activity.id}
                            className="flex items-start gap-3 p-3 rounded-xl hover:bg-surface-800/40 transition-colors"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-surface-800/60 flex items-center justify-center">
                              {getActivityIcon(activity.action)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-surface-200">
                                  {formatActivityAction(activity.action)}
                                </span>
                                {activity.metadata?.jobId != null && (
                                  <Link
                                    to={`/jobs/${String(activity.metadata.jobId)}`}
                                    className="text-xs font-mono text-gpu-400 hover:text-gpu-300"
                                  >
                                    {String(activity.metadata.jobId).slice(0, 8)}...
                                  </Link>
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <span className="text-xs text-surface-500 truncate">
                                  {activity.userEmail}
                                </span>
                                <span className="text-xs text-surface-600">·</span>
                                <span className="text-xs text-surface-500">
                                  {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Pagination */}
                      {totalActivityPages > 1 && (
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-surface-800/40">
                          <button
                            onClick={() => setActivityPage((p) => Math.max(0, p - 1))}
                            disabled={activityPage === 0}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-surface-400 hover:text-surface-200 bg-surface-800/50 hover:bg-surface-800 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            <ChevronLeft className="w-3.5 h-3.5" />
                            Prev
                          </button>
                          <span className="text-xs text-surface-500">
                            {activityPage + 1} / {totalActivityPages}
                          </span>
                          <button
                            onClick={() => setActivityPage((p) => Math.min(totalActivityPages - 1, p + 1))}
                            disabled={activityPage >= totalActivityPages - 1}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-surface-400 hover:text-surface-200 bg-surface-800/50 hover:bg-surface-800 rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                          >
                            Next
                            <ChevronRight className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.section>
            </div>
          </div>
        </motion.div>
    </motion.div>
  );
}

// Plus icon component for the header button
function Plus({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14M5 12h14" />
    </svg>
  );
}
